import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Users, Mail, BarChart3, Play, RefreshCw, CheckCircle, Send, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';


var authAxios = axios.create();
authAxios.interceptors.request.use(function(config) {
  var token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = 'Bearer ' + token;
  }
  return config;
});

export default function AdminOnboarding() {
  var [stats, setStats] = useState(null);
  var [profiles, setProfiles] = useState([]);
  var [loading, setLoading] = useState(true);
  var [entityType, setEntityType] = useState('center');
  var [filterStatus, setFilterStatus] = useState('all');
  var [triggering, setTriggering] = useState(false);

  useEffect(function() {
    loadStats();
  }, []);

  useEffect(function() {
    loadProfiles();
  }, [entityType, filterStatus]);

  async function loadStats() {
    try {
      var res = await authAxios.get(API + '/admin/onboarding/stats', { withCredentials: true });
      setStats(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar estadísticas de onboarding');
    }
  }

  async function loadProfiles() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/onboarding/profiles?entity_type=' + entityType + '&filter_status=' + filterStatus, { withCredentials: true });
      setProfiles(res.data.profiles || []);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar perfiles');
    }
    setLoading(false);
  }

  async function triggerJob() {
    setTriggering(true);
    try {
      await authAxios.post(API + '/admin/onboarding/trigger-job', {}, { withCredentials: true });
      toast.success('Job de onboarding ejecutado');
      loadStats();
      loadProfiles();
    } catch (e) {
      console.error(e);
      toast.error('Error al ejecutar job');
    }
    setTriggering(false);
  }

  async function sendManualEmail(profile, template) {
    try {
      await authAxios.post(API + '/admin/onboarding/send-email', {
        entity_id: profile.id,
        entity_type: entityType,
        template: template
      }, { withCredentials: true });
      toast.success('Email enviado a ' + profile.email);
      loadProfiles();
    } catch (e) {
      console.error(e);
      toast.error('Error al enviar email');
    }
  }

  function getCompletionColor(percentage) {
    if (percentage >= 80) return 'text-emerald-400';
    if (percentage >= 50) return 'text-amber-400';
    return 'text-red-400';
  }

  function getCompletionBg(percentage) {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  }

  var entityLabels = {
    center: 'Centros',
    professional: 'Profesionales',
    workshop: 'Talleres'
  };

  var templateLabels = {
    onboarding_center_description: 'Descripción',
    onboarding_center_photos: 'Fotos',
    onboarding_center_services: 'Servicios',
    onboarding_center_help: 'Ayuda',
    onboarding_center_final: 'Final',
    onboarding_professional_bio: 'Bio',
    onboarding_professional_credentials: 'Credenciales',
    onboarding_professional_schedule: 'Horarios',
    onboarding_professional_help: 'Ayuda',
    onboarding_workshop_description: 'Descripción',
    onboarding_workshop_photos: 'Fotos',
    onboarding_workshop_help: 'Ayuda'
  };

  var sequenceTemplates = {
    center: ['onboarding_center_description', 'onboarding_center_photos', 'onboarding_center_services', 'onboarding_center_help', 'onboarding_center_final'],
    professional: ['onboarding_professional_bio', 'onboarding_professional_credentials', 'onboarding_professional_schedule', 'onboarding_professional_help'],
    workshop: ['onboarding_workshop_description', 'onboarding_workshop_photos', 'onboarding_workshop_help']
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-50">Secuencias de Onboarding</h2>
          <p className="text-slate-400">Emails automáticos para completar perfiles</p>
        </div>
        <Button
          onClick={triggerJob}
          disabled={triggering}
          className="bg-cyan-600 hover:bg-cyan-700"
        >
          {triggering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          Ejecutar Job Ahora
        </Button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-400">Perfiles con Cuenta</h3>
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xl font-bold text-slate-50">{stats.entities.centers}</p>
                  <p className="text-xs text-slate-500">Centros</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-50">{stats.entities.professionals}</p>
                  <p className="text-xs text-slate-500">Profesionales</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-50">{stats.entities.workshops}</p>
                  <p className="text-xs text-slate-500">Talleres</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-400">Emails Enviados</h3>
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xl font-bold text-slate-50">{stats.emails_sent.centers}</p>
                  <p className="text-xs text-slate-500">Centros</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-50">{stats.emails_sent.professionals}</p>
                  <p className="text-xs text-slate-500">Profesionales</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-50">{stats.emails_sent.workshops}</p>
                  <p className="text-xs text-slate-500">Talleres</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-400">Completitud Promedio</h3>
                <BarChart3 className="w-5 h-5 text-amber-400" />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className={'text-xl font-bold ' + getCompletionColor(stats.avg_completion.centers)}>{stats.avg_completion.centers}%</p>
                  <p className="text-xs text-slate-500">Centros</p>
                </div>
                <div>
                  <p className={'text-xl font-bold ' + getCompletionColor(stats.avg_completion.professionals)}>{stats.avg_completion.professionals}%</p>
                  <p className="text-xs text-slate-500">Profesionales</p>
                </div>
                <div>
                  <p className={'text-xl font-bold ' + getCompletionColor(stats.avg_completion.workshops)}>{stats.avg_completion.workshops}%</p>
                  <p className="text-xs text-slate-500">Talleres</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Logs */}
      {stats && stats.recent_logs && stats.recent_logs.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-slate-50 text-lg">Últimas Ejecuciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.recent_logs.slice(0, 5).map(function(log, idx) {
                var date = new Date(log.executed_at);
                return (
                  <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-800/50 rounded">
                    <span className="text-slate-400">{date.toLocaleDateString('es-CL')} {date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="text-slate-300">
                      {log.emails_sent} emails enviados
                      {log.details && (
                        <span className="text-slate-500 ml-2">
                          (C:{log.details.centers || 0} P:{log.details.professionals || 0} T:{log.details.workshops || 0})
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex gap-2">
          {['center', 'professional', 'workshop'].map(function(type) {
            return (
              <Button
                key={type}
                size="sm"
                variant={entityType === type ? 'default' : 'outline'}
                onClick={function() { setEntityType(type); }}
                className={entityType === type ? 'bg-cyan-600 hover:bg-cyan-700' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
              >
                {entityLabels[type]}
              </Button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            onClick={function() { setFilterStatus('all'); }}
            className={filterStatus === 'all' ? 'bg-slate-700' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}
          >
            Todos
          </Button>
          <Button
            size="sm"
            variant={filterStatus === 'incomplete' ? 'default' : 'outline'}
            onClick={function() { setFilterStatus('incomplete'); }}
            className={filterStatus === 'incomplete' ? 'bg-amber-600' : 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10'}
          >
            Incompletos (&lt;80%)
          </Button>
          <Button
            size="sm"
            variant={filterStatus === 'complete' ? 'default' : 'outline'}
            onClick={function() { setFilterStatus('complete'); }}
            className={filterStatus === 'complete' ? 'bg-emerald-600' : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'}
          >
            Completos (&ge;80%)
          </Button>
        </div>
      </div>

      {/* Profiles List */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-50 flex items-center justify-between">
            <span>{entityLabels[entityType]} ({profiles.length})</span>
            <Button size="sm" variant="ghost" onClick={loadProfiles} className="text-slate-400 hover:text-slate-200">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
          ) : profiles.length === 0 ? (
            <p className="text-slate-500 text-center py-6">No hay perfiles que mostrar</p>
          ) : (
            <div className="space-y-3">
              {profiles.map(function(profile) {
                var templates = sequenceTemplates[entityType] || [];
                return (
                  <div key={profile.id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-slate-100">{profile.name}</h4>
                        <p className="text-sm text-slate-400">{profile.email || 'Sin email'}</p>
                        {profile.city && <p className="text-xs text-slate-500">{profile.city}</p>}
                      </div>
                      <div className="text-right">
                        <p className={'text-lg font-bold ' + getCompletionColor(profile.completion.percentage)}>
                          {profile.completion.percentage}%
                        </p>
                        <p className="text-xs text-slate-500">Día {profile.days_since_creation}</p>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-3">
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={'h-full transition-all ' + getCompletionBg(profile.completion.percentage)}
                          style={{ width: profile.completion.percentage + '%' }}
                        />
                      </div>
                    </div>

                    {/* Missing fields */}
                    {profile.completion.missing && profile.completion.missing.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-slate-500 mb-1">Campos faltantes:</p>
                        <div className="flex flex-wrap gap-1">
                          {profile.completion.missing.map(function(field) {
                            return (
                              <span key={field} className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
                                {field.replace(/_/g, ' ')}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Email sequence status */}
                    <div className="flex flex-wrap gap-2">
                      {templates.map(function(template) {
                        var isSent = profile.emails_sent && profile.emails_sent.includes(template);
                        return (
                          <div key={template} className="flex items-center gap-1">
                            <span className={'px-2 py-1 text-xs rounded ' + (isSent ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400')}>
                              {isSent ? <CheckCircle className="w-3 h-3 inline mr-1" /> : null}
                              {templateLabels[template] || template}
                            </span>
                            {!isSent && profile.email && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-1 text-slate-500 hover:text-cyan-400"
                                onClick={function() { sendManualEmail(profile, template); }}
                              >
                                <Send className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
